import React, { useCallback, useEffect, useState } from 'react';
import {
  HiStar,
  HiUsers,
  HiGift,
  HiSpeakerphone,
  HiClipboardList,
  HiTrendingUp,
  HiCash,
  HiRefresh,
  HiCheck,
  HiX,
  HiSearch,
  HiCog,
} from 'react-icons/hi';
import apiService from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import './Rewards.css';

const TABS = [
  { id: 'dashboard', label: 'Visão geral', icon: HiTrendingUp },
  { id: 'accounts', label: 'Clientes', icon: HiUsers },
  { id: 'campaigns', label: 'Campanhas', icon: HiSpeakerphone },
  { id: 'catalog', label: 'Catálogo', icon: HiGift },
  { id: 'missions', label: 'Missões', icon: HiClipboardList },
  { id: 'redemptions', label: 'Resgates', icon: HiCash },
  { id: 'ranking', label: 'Ranking', icon: HiStar },
  { id: 'settings', label: 'Configurações', icon: HiCog },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value) || 0
  );

const formatPoints = (value) => Number(value || 0).toLocaleString('pt-BR');

const Rewards = () => {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [missions, setMissions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [adjustForm, setAdjustForm] = useState({ points: '', description: '', type: 'bonus' });
  const [campaignForm, setCampaignForm] = useState({
    nome: '',
    multiplier: 2,
    starts_at: '',
    ends_at: '',
    ativo: true,
  });
  const [missionForm, setMissionForm] = useState({
    nome: '',
    type: 'shipments_count',
    target_value: 3,
    reward_points: 500,
    period: 'monthly',
  });
  const [catalogForm, setCatalogForm] = useState({
    nome: '',
    categoria: 'frete',
    points_cost: 1000,
    credit_value: '',
    descricao: '',
    ativo: true,
  });
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [catalogToDelete, setCatalogToDelete] = useState(null);
  const [deletingCatalog, setDeletingCatalog] = useState(false);
  const [message, setMessage] = useState('');

  const emptyCatalogForm = {
    nome: '',
    categoria: 'frete',
    points_cost: 1000,
    credit_value: '',
    descricao: '',
    ativo: true,
  };

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3500);
  };

  const loadDashboard = useCallback(async () => {
    const data = await apiService.getRewardsDashboard();
    setMetrics(data);
  }, []);

  const loadAccounts = useCallback(async () => {
    const res = await apiService.getRewardsAccounts({ search: search || undefined, per_page: 40 });
    setAccounts(res?.data || []);
  }, [search]);

  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'dashboard') {
        await loadDashboard();
        const t = await apiService.getRewardsTiers();
        setTiers(Array.isArray(t) ? t : []);
      } else if (tab === 'accounts') {
        await loadAccounts();
      } else if (tab === 'campaigns') {
        const c = await apiService.getRewardsCampaigns();
        setCampaigns(Array.isArray(c) ? c : []);
      } else if (tab === 'catalog') {
        const c = await apiService.getRewardsCatalog();
        setCatalog(Array.isArray(c) ? c : []);
      } else if (tab === 'missions') {
        const m = await apiService.getRewardsMissions();
        setMissions(Array.isArray(m) ? m : []);
      } else if (tab === 'redemptions') {
        const r = await apiService.getRewardsRedemptions({ per_page: 40 });
        const list = r?.data?.data || r?.data || [];
        setRedemptions(Array.isArray(list) ? list : []);
      } else if (tab === 'ranking') {
        const rk = await apiService.getRewardsRanking({ limit: 100 });
        setRanking(Array.isArray(rk) ? rk : []);
      } else if (tab === 'settings') {
        const s = await apiService.getRewardsSettings();
        setSettings(s);
      }
    } catch (err) {
      flash(err.message || 'Erro ao carregar SendingTo Rewards');
    } finally {
      setLoading(false);
    }
  }, [tab, loadDashboard, loadAccounts]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const openAccount = async (id) => {
    try {
      const res = await apiService.getRewardsAccount(id);
      setSelected(res);
    } catch (err) {
      flash(err.message || 'Erro ao abrir conta');
    }
  };

  const submitAdjust = async () => {
    if (!selected?.data?.id) return;
    try {
      await apiService.adjustRewardsPoints(selected.data.id, {
        points: Number(adjustForm.points),
        description: adjustForm.description,
        type: adjustForm.type,
      });
      flash('Pontos atualizados');
      setAdjustForm({ points: '', description: '', type: 'bonus' });
      await openAccount(selected.data.id);
      await loadAccounts();
    } catch (err) {
      flash(err.message || 'Falha ao ajustar pontos');
    }
  };

  const toggleVip = async () => {
    if (!selected?.data?.id) return;
    try {
      await apiService.setRewardsVip(selected.data.id, !selected.data.is_vip);
      flash(selected.data.is_vip ? 'VIP removido' : 'Cliente marcado como VIP');
      await openAccount(selected.data.id);
    } catch (err) {
      flash(err.message || 'Falha ao atualizar VIP');
    }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    try {
      await apiService.createRewardsCampaign(campaignForm);
      flash('Campanha criada');
      setCampaignForm({ nome: '', multiplier: 2, starts_at: '', ends_at: '', ativo: true });
      const c = await apiService.getRewardsCampaigns();
      setCampaigns(Array.isArray(c) ? c : []);
    } catch (err) {
      flash(err.message || 'Erro ao criar campanha');
    }
  };

  const createMission = async (e) => {
    e.preventDefault();
    try {
      await apiService.createRewardsMission(missionForm);
      flash('Missão criada');
      setMissionForm({
        nome: '',
        type: 'shipments_count',
        target_value: 3,
        reward_points: 500,
        period: 'monthly',
      });
      const m = await apiService.getRewardsMissions();
      setMissions(Array.isArray(m) ? m : []);
    } catch (err) {
      flash(err.message || 'Erro ao criar missão');
    }
  };

  const reloadCatalog = async () => {
    const c = await apiService.getRewardsCatalog();
    setCatalog(Array.isArray(c) ? c : []);
  };

  const closeCatalogModal = () => {
    setCatalogModalOpen(false);
    setEditingCatalogId(null);
    setCatalogForm({ ...emptyCatalogForm });
  };

  const openNewCatalogModal = () => {
    setEditingCatalogId(null);
    setCatalogForm({ ...emptyCatalogForm });
    setCatalogModalOpen(true);
  };

  const startEditCatalog = (item) => {
    setEditingCatalogId(item.id);
    setCatalogForm({
      nome: item.nome || '',
      categoria: item.categoria || 'frete',
      points_cost: item.points_cost ?? 1000,
      credit_value: item.credit_value != null ? String(item.credit_value) : '',
      descricao: item.descricao || '',
      ativo: item.ativo !== false,
    });
    setCatalogModalOpen(true);
  };

  const saveCatalog = async (e) => {
    e.preventDefault();
    const payload = {
      nome: catalogForm.nome,
      categoria: catalogForm.categoria,
      descricao: catalogForm.descricao || null,
      credit_value: catalogForm.credit_value ? Number(catalogForm.credit_value) : null,
      points_cost: Number(catalogForm.points_cost),
      ativo: !!catalogForm.ativo,
    };
    try {
      if (editingCatalogId) {
        await apiService.updateRewardsCatalog(editingCatalogId, payload);
        flash('Item do catálogo atualizado');
      } else {
        await apiService.createRewardsCatalog(payload);
        flash('Item adicionado ao catálogo');
      }
      closeCatalogModal();
      await reloadCatalog();
    } catch (err) {
      flash(err.message || (editingCatalogId ? 'Erro ao atualizar item' : 'Erro ao criar item'));
    }
  };

  const deleteCatalog = async (item) => {
    setCatalogToDelete(item);
  };

  const confirmDeleteCatalog = async () => {
    if (!catalogToDelete) return;
    setDeletingCatalog(true);
    try {
      await apiService.deleteRewardsCatalog(catalogToDelete.id);
      flash('Item removido do catálogo');
      if (editingCatalogId === catalogToDelete.id) closeCatalogModal();
      setCatalogToDelete(null);
      await reloadCatalog();
    } catch (err) {
      flash(err.message || 'Erro ao excluir item');
    } finally {
      setDeletingCatalog(false);
    }
  };

  const fulfill = async (id, status) => {
    try {
      await apiService.fulfillRewardsRedemption(id, { status });
      flash(status === 'fulfilled' ? 'Resgate concluído' : 'Resgate cancelado');
      const r = await apiService.getRewardsRedemptions({ per_page: 40 });
      const list = r?.data?.data || r?.data || [];
      setRedemptions(Array.isArray(list) ? list : []);
    } catch (err) {
      flash(err.message || 'Erro ao atualizar resgate');
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const updated = await apiService.updateRewardsSettings(settings);
      setSettings(updated);
      flash('Configurações salvas');
    } catch (err) {
      flash(err.message || 'Erro ao salvar');
    }
  };

  return (
    <div className="page-container rewards-page">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>SendingTo Rewards</span>
      </div>

      <section className="rewards-hero">
        <div className="rewards-hero-left">
          <div className="rewards-hero-icon">
            <HiStar size={28} />
          </div>
          <div>
            <h1>SendingTo Rewards</h1>
            <p>
              Pontos, níveis, missões, indicação e cashback — acompanhe o programa de fidelidade
              SendingTo Club.
            </p>
          </div>
        </div>
        <div className="rewards-header-actions">
          <button type="button" className="btn-rw-ghost" onClick={loadTabData}>
            <HiRefresh size={18} /> Atualizar
          </button>
          <button
            type="button"
            className="btn-rw-primary"
            onClick={() => {
              setSelected(null);
              setTab('accounts');
            }}
          >
            <HiUsers size={18} /> Ver clientes
          </button>
        </div>
      </section>

      {message ? <div className="rewards-flash">{message}</div> : null}

      {metrics && (
        <div className="rewards-kpis">
          <div className="rewards-kpi rewards-kpi--accounts">
            <div className="rewards-kpi-label">
              <span>Contas ativas</span>
              <i className="rewards-kpi-dot" />
            </div>
            <strong>{formatPoints(metrics.accounts)}</strong>
            <small>{formatPoints(metrics.vip)} VIP no programa</small>
          </div>
          <div className="rewards-kpi rewards-kpi--points">
            <div className="rewards-kpi-label">
              <span>Pontos em circulação</span>
              <i className="rewards-kpi-dot" />
            </div>
            <strong>{formatPoints(metrics.points_in_circulation)}</strong>
            <small>{formatPoints(metrics.earned_today)} ganhos hoje</small>
          </div>
          <div className="rewards-kpi rewards-kpi--cashback">
            <div className="rewards-kpi-label">
              <span>Cashback total</span>
              <i className="rewards-kpi-dot" />
            </div>
            <strong>{formatCurrency(metrics.cashback_balance)}</strong>
            <small>{formatPoints(metrics.redemptions_pending)} resgate(s) pendente(s)</small>
          </div>
        </div>
      )}

      <div className="rewards-toolbar">
        <div className="rewards-filters">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                className={`rewards-filter ${tab === t.id ? 'active' : ''}`}
                onClick={() => {
                  setSelected(null);
                  setTab(t.id);
                }}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="rewards-panel">
          <div className="rewards-loading">Carregando SendingTo Rewards…</div>
        </div>
      ) : (
        <>
          {tab === 'dashboard' && metrics && (
            <div className="rewards-panel rewards-panel-pad">
              <h3>Distribuição por nível</h3>
              <div className="rewards-tier-bars">
                {(metrics.by_tier || []).map((row) => (
                  <div key={row.slug || row.tier} className="rewards-tier-row">
                    <span>{row.tier}</span>
                    <div className="bar">
                      <i
                        style={{
                          width: `${Math.min(
                            100,
                            (row.total / Math.max(metrics.accounts || 1, 1)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                    <strong>{row.total}</strong>
                  </div>
                ))}
              </div>
              <div className="rewards-tier-legend">
                {tiers.map((t) => (
                  <span key={t.id} style={{ borderColor: t.cor || '#0f766e' }}>
                    {t.nome} · {t.desconto_percent}% · {t.min_envios}+ envios /{' '}
                    {formatCurrency(t.min_gasto)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {tab === 'accounts' && (
            <section className="rewards-split">
              <div className="rewards-panel">
                <div className="rewards-panel-pad" style={{ paddingBottom: 0 }}>
                  <div className="rewards-toolbar" style={{ marginBottom: '0.85rem' }}>
                    <div className="search-box">
                      <HiSearch className="search-icon" size={20} />
                      <input
                        placeholder="Buscar cliente, e-mail ou código"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadAccounts()}
                      />
                    </div>
                    <button type="button" className="btn-rw-muted" onClick={loadAccounts}>
                      Buscar
                    </button>
                  </div>
                </div>
                {accounts.length === 0 ? (
                  <div className="rewards-empty">
                    <div className="rewards-empty-icon">
                      <HiUsers size={28} />
                    </div>
                    <h3>Nenhuma conta encontrada</h3>
                    <p>Ajuste a busca ou aguarde o primeiro cliente no programa.</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Cliente</th>
                          <th>Nível</th>
                          <th>Pontos</th>
                          <th>Cashback</th>
                          <th>Envios</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map((a) => (
                          <tr
                            key={a.id}
                            onClick={() => openAccount(a.id)}
                            className={selected?.data?.id === a.id ? 'selected' : ''}
                          >
                            <td className="rewards-title-cell">
                              <strong>{a.cliente?.nome}</strong>
                              <span className="rewards-id-chip">
                                {a.referral_code}
                                {a.is_vip ? ' · VIP' : ''}
                              </span>
                            </td>
                            <td>
                              <span className="rewards-badge info">{a.tier?.nome || '—'}</span>
                            </td>
                            <td className="rewards-amount">{formatPoints(a.points_balance)}</td>
                            <td className="rewards-amount">{formatCurrency(a.cashback_balance)}</td>
                            <td>{a.total_shipments}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {selected?.data && (
                <aside className="rewards-panel rewards-panel-pad">
                  <h3>{selected.data.cliente?.nome}</h3>
                  <p className="rewards-muted">
                    {selected.data.tier?.nome} · {formatPoints(selected.data.points_balance)} pts
                    {selected.data.next_tier
                      ? ` · próximo: ${selected.data.next_tier.nome} (faltam ${selected.data.next_tier.faltam_envios} envios)`
                      : ''}
                  </p>
                  <div className="rewards-detail-meta">
                    <p>
                      <strong>Código</strong>
                      {selected.data.referral_code}
                    </p>
                    <p>
                      <strong>Indicações</strong>
                      {selected.data.referral_count}
                    </p>
                  </div>
                  <button type="button" className="btn-rw-muted" onClick={toggleVip}>
                    {selected.data.is_vip ? 'Remover VIP' : 'Marcar VIP / Diamante'}
                  </button>

                  <h4>Ajuste / bônus / cancelamento</h4>
                  <div className="rewards-form-grid">
                    <label>
                      Tipo
                      <select
                        value={adjustForm.type}
                        onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                      >
                        <option value="bonus">Bônus (+)</option>
                        <option value="adjust">Ajuste (+/−)</option>
                        <option value="cancel">Cancelar pontos (−)</option>
                      </select>
                    </label>
                    <label>
                      Pontos
                      <input
                        type="number"
                        placeholder="Ex: 500"
                        value={adjustForm.points}
                        onChange={(e) => setAdjustForm({ ...adjustForm, points: e.target.value })}
                      />
                    </label>
                    <label>
                      Motivo
                      <input
                        placeholder="Descrição do ajuste"
                        value={adjustForm.description}
                        onChange={(e) =>
                          setAdjustForm({ ...adjustForm, description: e.target.value })
                        }
                      />
                    </label>
                    <button type="button" className="btn-rw-primary" onClick={submitAdjust}>
                      Aplicar
                    </button>
                  </div>

                  <h4>Extrato recente</h4>
                  <ul className="rewards-ledger">
                    {(selected.ledger || []).slice(0, 12).map((l) => (
                      <li key={l.id}>
                        <span className={l.points >= 0 ? 'pos' : 'neg'}>
                          {l.points >= 0 ? '+' : ''}
                          {l.points}
                        </span>
                        <div>
                          <strong>{l.description || l.type}</strong>
                          <small>{new Date(l.created_at).toLocaleString('pt-BR')}</small>
                        </div>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
            </section>
          )}

          {tab === 'campaigns' && (
            <section className="rewards-split">
              <form className="rewards-panel rewards-panel-pad" onSubmit={createCampaign}>
                <h3>Nova campanha</h3>
                <div className="rewards-form-grid">
                  <label>
                    Nome
                    <input
                      required
                      placeholder="Ex: Dobro de pontos em agosto"
                      value={campaignForm.nome}
                      onChange={(e) => setCampaignForm({ ...campaignForm, nome: e.target.value })}
                    />
                  </label>
                  <label>
                    Multiplicador
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      value={campaignForm.multiplier}
                      onChange={(e) =>
                        setCampaignForm({ ...campaignForm, multiplier: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    Início
                    <input
                      type="datetime-local"
                      value={campaignForm.starts_at}
                      onChange={(e) =>
                        setCampaignForm({ ...campaignForm, starts_at: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Fim
                    <input
                      type="datetime-local"
                      value={campaignForm.ends_at}
                      onChange={(e) =>
                        setCampaignForm({ ...campaignForm, ends_at: e.target.value })
                      }
                    />
                  </label>
                  <button type="submit" className="btn-rw-primary">
                    Criar campanha
                  </button>
                </div>
              </form>
              <div className="rewards-panel rewards-panel-pad">
                <h3>Campanhas</h3>
                {campaigns.length === 0 ? (
                  <div className="rewards-empty" style={{ padding: '2rem 0' }}>
                    <p>Nenhuma campanha cadastrada.</p>
                  </div>
                ) : (
                  <ul className="rewards-list">
                    {campaigns.map((c) => (
                      <li key={c.id}>
                        <div>
                          <strong>{c.nome}</strong>
                          <span>
                            ×{c.multiplier} · {c.ativo ? 'Ativa' : 'Inativa'}
                          </span>
                        </div>
                        <span className={`rewards-badge ${c.ativo ? 'success' : 'neutral'}`}>
                          {c.ativo ? 'Ativa' : 'Off'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {tab === 'catalog' && (
            <section className="rewards-panel">
              <div className="rewards-panel-pad rewards-catalog-head">
                <div>
                  <h3>Catálogo</h3>
                  <p className="rewards-muted" style={{ margin: '4px 0 0' }}>
                    Itens disponíveis para resgate no app SendingTo Club
                  </p>
                </div>
                <button type="button" className="btn-rw-primary" onClick={openNewCatalogModal}>
                  Novo item
                </button>
              </div>

              {catalog.length === 0 ? (
                <div className="rewards-empty" style={{ padding: '2.5rem 1.25rem' }}>
                  <p>Nenhum item no catálogo. Clique em Novo item para adicionar.</p>
                </div>
              ) : (
                <div className="rewards-catalog-grid">
                  {catalog.map((item) => (
                    <article key={item.id} className="rewards-catalog-card">
                      <div className="rewards-catalog-card__top">
                        <span className="rewards-badge neutral">{item.categoria}</span>
                        <span
                          className={`rewards-badge ${item.ativo ? 'success' : 'neutral'}`}
                        >
                          {item.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <h4 className="rewards-catalog-card__title">{item.nome}</h4>
                      {item.descricao ? (
                        <p className="rewards-catalog-card__desc">{item.descricao}</p>
                      ) : (
                        <p className="rewards-catalog-card__desc rewards-muted">Sem descrição</p>
                      )}
                      <div className="rewards-catalog-card__meta">
                        <strong>{formatPoints(item.points_cost)}</strong>
                        <span>pontos</span>
                        {item.credit_value != null && Number(item.credit_value) > 0 ? (
                          <span className="rewards-catalog-card__credit">
                            · {formatCurrency(item.credit_value)}
                          </span>
                        ) : null}
                      </div>
                      <div className="rewards-catalog-card__actions">
                        <button
                          type="button"
                          className="btn-rw-edit"
                          onClick={() => startEditCatalog(item)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn-rw-delete"
                          onClick={() => deleteCatalog(item)}
                        >
                          Excluir
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {catalogModalOpen ? (
                <div
                  className="rewards-modal-overlay"
                  role="presentation"
                  onClick={closeCatalogModal}
                >
                  <div
                    className="rewards-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="rewards-catalog-modal-title"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <header className="rewards-modal__header">
                      <h3 id="rewards-catalog-modal-title">
                        {editingCatalogId ? 'Editar item de resgate' : 'Novo item de resgate'}
                      </h3>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label="Fechar"
                        onClick={closeCatalogModal}
                      >
                        <HiX size={18} />
                      </button>
                    </header>
                    <form className="rewards-modal__body" onSubmit={saveCatalog}>
                      <div className="rewards-form-grid two">
                        <label>
                          Nome
                          <input
                            required
                            placeholder="Nome do item"
                            value={catalogForm.nome}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, nome: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Categoria
                          <select
                            value={catalogForm.categoria}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, categoria: e.target.value })
                            }
                          >
                            <option value="frete">Frete</option>
                            <option value="seguro">Seguro</option>
                            <option value="embalagem">Embalagem</option>
                            <option value="coleta">Coleta</option>
                            <option value="prioridade">Prioridade</option>
                            <option value="brinde">Brinde</option>
                            <option value="credito">Crédito</option>
                            <option value="parceiro">Parceiro</option>
                          </select>
                        </label>
                        <label>
                          Custo em pontos
                          <input
                            type="number"
                            required
                            min={1}
                            value={catalogForm.points_cost}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, points_cost: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Valor crédito (opcional)
                          <input
                            type="number"
                            placeholder="Ex: 100"
                            value={catalogForm.credit_value}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, credit_value: e.target.value })
                            }
                          />
                        </label>
                        <label style={{ gridColumn: '1 / -1' }}>
                          Descrição
                          <textarea
                            placeholder="Detalhes do benefício"
                            value={catalogForm.descricao}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, descricao: e.target.value })
                            }
                          />
                        </label>
                        <label className="rw-switch">
                          <input
                            type="checkbox"
                            checked={!!catalogForm.ativo}
                            onChange={(e) =>
                              setCatalogForm({ ...catalogForm, ativo: e.target.checked })
                            }
                          />
                          <span className="rw-switch__ui" aria-hidden="true" />
                          <span className="rw-switch__text">Ativo no app</span>
                        </label>
                      </div>
                      <footer className="rewards-modal__footer">
                        <button type="button" className="btn-rw-muted" onClick={closeCatalogModal}>
                          Cancelar
                        </button>
                        <button type="submit" className="btn-rw-primary">
                          {editingCatalogId ? 'Salvar alterações' : 'Adicionar item'}
                        </button>
                      </footer>
                    </form>
                  </div>
                </div>
              ) : null}
            </section>
          )}

          {tab === 'missions' && (
            <section className="rewards-split">
              <form className="rewards-panel rewards-panel-pad" onSubmit={createMission}>
                <h3>Nova missão</h3>
                <div className="rewards-form-grid">
                  <label>
                    Nome
                    <input
                      required
                      placeholder="Ex: 3 cargas este mês"
                      value={missionForm.nome}
                      onChange={(e) => setMissionForm({ ...missionForm, nome: e.target.value })}
                    />
                  </label>
                  <label>
                    Tipo
                    <select
                      value={missionForm.type}
                      onChange={(e) => setMissionForm({ ...missionForm, type: e.target.value })}
                    >
                      <option value="shipments_count">Qtd. de envios</option>
                      <option value="countries_count">Países diferentes</option>
                      <option value="kg_total">Kg totais</option>
                    </select>
                  </label>
                  <label>
                    Meta
                    <input
                      type="number"
                      value={missionForm.target_value}
                      onChange={(e) =>
                        setMissionForm({ ...missionForm, target_value: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    Pontos de recompensa
                    <input
                      type="number"
                      value={missionForm.reward_points}
                      onChange={(e) =>
                        setMissionForm({ ...missionForm, reward_points: Number(e.target.value) })
                      }
                    />
                  </label>
                  <label>
                    Período
                    <select
                      value={missionForm.period}
                      onChange={(e) => setMissionForm({ ...missionForm, period: e.target.value })}
                    >
                      <option value="monthly">Mensal</option>
                      <option value="yearly">Anual</option>
                      <option value="lifetime">Vitalícia</option>
                      <option value="once">Única</option>
                    </select>
                  </label>
                  <button type="submit" className="btn-rw-primary">
                    Criar missão
                  </button>
                </div>
              </form>
              <div className="rewards-panel rewards-panel-pad">
                <h3>Missões</h3>
                <ul className="rewards-list">
                  {missions.map((m) => (
                    <li key={m.id}>
                      <div>
                        <strong>{m.nome}</strong>
                        <span>
                          Meta {m.target_value} · {formatPoints(m.reward_points)} pts · {m.period}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {tab === 'redemptions' && (
            <div className="rewards-panel">
              <div className="rewards-panel-pad" style={{ paddingBottom: 0 }}>
                <h3>Resgates</h3>
              </div>
              {redemptions.length === 0 ? (
                <div className="rewards-empty">
                  <div className="rewards-empty-icon">
                    <HiGift size={28} />
                  </div>
                  <h3>Nenhum resgate</h3>
                  <p>Quando clientes trocarem pontos, os pedidos aparecem aqui.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Item</th>
                        <th>Pontos</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redemptions.map((r) => (
                        <tr key={r.id} style={{ cursor: 'default' }}>
                          <td>{r.account?.cliente?.nome || r.reward_account_id}</td>
                          <td>{r.catalog_item?.nome || r.catalogItem?.nome || '—'}</td>
                          <td className="rewards-amount">{formatPoints(r.points_spent)}</td>
                          <td>
                            <span
                              className={`rewards-badge ${
                                r.status === 'fulfilled'
                                  ? 'success'
                                  : r.status === 'cancelled'
                                    ? 'neutral'
                                    : 'warning'
                              }`}
                            >
                              {r.status}
                            </span>
                          </td>
                          <td>
                            {r.status === 'pending' && (
                              <div className="rewards-actions">
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => fulfill(r.id, 'fulfilled')}
                                  title="Concluir"
                                >
                                  <HiCheck size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-rw-danger"
                                  onClick={() => fulfill(r.id, 'cancelled')}
                                  title="Cancelar"
                                >
                                  <HiX size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'ranking' && (
            <div className="rewards-panel">
              <div className="rewards-panel-pad" style={{ paddingBottom: 0 }}>
                <h3>Top 100 do ano</h3>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th>Nível</th>
                      <th>Pontos no ano</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((r) => (
                      <tr key={r.account_id} style={{ cursor: 'default' }}>
                        <td>
                          <span className="rewards-badge gold">#{r.rank}</span>
                        </td>
                        <td className="rewards-title-cell">
                          <strong>{r.nome}</strong>
                        </td>
                        <td>
                          <span className="rewards-badge info">{r.tier || '—'}</span>
                        </td>
                        <td className="rewards-amount">{formatPoints(r.year_points)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'settings' && settings && (
            <form className="rewards-panel rewards-panel-pad" onSubmit={saveSettings}>
              <h3>Regras do programa</h3>
              <div className="rewards-form-grid two">
                {[
                  ['pontos_por_real', 'Pontos por R$1'],
                  ['pontos_por_kg', 'Pontos por kg'],
                  ['pontos_indicacao', 'Pontos por indicação'],
                  ['pontos_avaliacao', 'Pontos por avaliação'],
                  ['cashback_percent', 'Cashback %'],
                  ['cashback_validade_dias', 'Validade cashback (dias)'],
                  ['credito_por_pontos', 'Crédito por ponto (R$)'],
                  ['desconto_amigo_primeiro_envio', 'Desconto amigo 1º envio %'],
                  ['indicacoes_para_envio_gratis', 'Indicações p/ envio grátis'],
                  ['envio_gratis_max_kg', 'Envio grátis até (kg)'],
                ].map(([key, label]) => (
                  <label key={key}>
                    {label}
                    <input
                      type="number"
                      step="any"
                      value={settings[key] ?? ''}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                    />
                  </label>
                ))}
                <label>
                  Início da pontuação
                  <input
                    type="date"
                    value={settings.pontos_inicio_em ? String(settings.pontos_inicio_em).slice(0, 10) : ''}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        pontos_inicio_em: e.target.value || null,
                      })
                    }
                  />
                  <small style={{ display: 'block', marginTop: 6, color: '#64748b' }}>
                    Só gera pontos automáticos em cargas pagas a partir desta data. Sem data, não credita.
                  </small>
                </label>
                <label className="rw-switch">
                  <input
                    type="checkbox"
                    checked={!!settings.aniversario_pontos_dobro}
                    onChange={(e) =>
                      setSettings({ ...settings, aniversario_pontos_dobro: e.target.checked })
                    }
                  />
                  <span className="rw-switch__ui" aria-hidden="true" />
                  <span className="rw-switch__text">Pontos em dobro no mês de aniversário</span>
                </label>
                <label className="rw-switch">
                  <input
                    type="checkbox"
                    checked={!!settings.programa_ativo}
                    onChange={(e) =>
                      setSettings({ ...settings, programa_ativo: e.target.checked })
                    }
                  />
                  <span className="rw-switch__ui" aria-hidden="true" />
                  <span className="rw-switch__text">Programa ativo</span>
                </label>
              </div>
              <div style={{ marginTop: '1.25rem' }}>
                <button type="submit" className="btn-rw-primary">
                  Salvar configurações
                </button>
              </div>
            </form>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!catalogToDelete}
        title="Excluir do catálogo"
        message={
          catalogToDelete
            ? `Excluir "${catalogToDelete.nome}" do catálogo? Essa ação não pode ser desfeita.`
            : ''
        }
        onConfirm={confirmDeleteCatalog}
        onCancel={() => {
          if (deletingCatalog) return;
          setCatalogToDelete(null);
        }}
        confirmText={deletingCatalog ? 'Excluindo…' : 'Excluir'}
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default Rewards;
